/**
 * Edit all of a project's core fields — title, client, priority, the requested
 * window + hard deadline, the site (address, contact, notes), the intake notes,
 * and the money figures (PM allocated budget + actual cost). Supervisor/admin
 * only; available at any status. Persists through PATCH /projects/:id
 * (useUpdateProject). The people/contacts live in EditPeopleDialog — this is
 * the everything-else editor.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import type {
  AddressSuggestion,
  Priority,
  Project,
  ProjectUpdateInput,
} from '@vari-crew/schemas';
import { useUpdateProject } from '@/api/projects';
import { AddressAutocomplete } from '@/components/ui/AddressAutocomplete';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input, Label, Textarea } from '@/components/ui/Input';

/** ISO timestamp → 'yyyy-mm-dd' for a <input type="date">; '' when null. */
function isoToDateInput(iso: string | null | undefined): string {
  if (!iso) return '';
  // Slice the date portion off the ISO string — these are date-only values
  // (no meaningful time-of-day), so avoid Date() so a TZ shift can't roll the day.
  return iso.slice(0, 10);
}

/**
 * 'yyyy-mm-dd' from a date input → an ISO string at BUSINESS-LOCAL midnight, or
 * null. Matches every other date-only writer (dayToIso, the AI extractor) so the
 * day reads back identically through the local readers (isoToDay, the server's
 * toBusinessDayString month calendar) as well as the UTC-part readers
 * (formatDateOnly). Storing UTC midnight here read a day early for US users on
 * those local readers — the exact off-by-one formatDateOnly was meant to kill
 * (review #10).
 */
function dateInputToIso(value: string): string | null {
  if (!value) return null;
  return new Date(`${value}T00:00:00`).toISOString();
}

/** Integer cents → a dollars string for a text input ('' when null). */
function centsToDollars(cents: number | null | undefined): string {
  if (cents == null) return '';
  return (cents / 100).toString();
}

/** A dollars string → integer cents (rounded), or null when blank/invalid. */
function dollarsToCents(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}

const PRIORITY_OPTIONS: { value: Priority; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

export function EditProjectDialog({
  project,
  open,
  onClose,
}: {
  project: Project;
  open: boolean;
  onClose: () => void;
}) {
  const update = useUpdateProject(project.id);

  const [title, setTitle] = useState(project.title);
  const [clientName, setClientName] = useState(project.client_name);
  const [priority, setPriority] = useState<Priority>(project.priority);
  const [startDate, setStartDate] = useState(isoToDateInput(project.requested_start_date));
  const [endDate, setEndDate] = useState(isoToDateInput(project.requested_end_date));
  const [deadline, setDeadline] = useState(isoToDateInput(project.deadline_date));
  const [line1, setLine1] = useState(project.site_address?.line1 ?? '');
  const [line2, setLine2] = useState(project.site_address?.line2 ?? '');
  const [city, setCity] = useState(project.site_address?.city ?? '');
  const [state, setState] = useState(project.site_address?.state ?? '');
  const [postalCode, setPostalCode] = useState(project.site_address?.postal_code ?? '');
  // Geocoded coordinates — preserved from the saved address, refreshed when the
  // user picks an autocomplete suggestion (so the map matches the new address).
  const [latitude, setLatitude] = useState<number | null>(project.site_address?.latitude ?? null);
  const [longitude, setLongitude] = useState<number | null>(
    project.site_address?.longitude ?? null,
  );
  const [contactName, setContactName] = useState(project.site_contact_name ?? '');
  const [contactPhone, setContactPhone] = useState(project.site_contact_phone ?? '');
  const [siteNotes, setSiteNotes] = useState(project.site_notes ?? '');
  const [intakeNotes, setIntakeNotes] = useState(project.intake_notes ?? '');
  const [budgetDollars, setBudgetDollars] = useState(
    centsToDollars(project.delivery_install_budget_cents),
  );
  const [actualDollars, setActualDollars] = useState(centsToDollars(project.actual_cost_cents));

  useEffect(() => {
    if (!open) return;
    setTitle(project.title);
    setClientName(project.client_name);
    setPriority(project.priority);
    setStartDate(isoToDateInput(project.requested_start_date));
    setEndDate(isoToDateInput(project.requested_end_date));
    setDeadline(isoToDateInput(project.deadline_date));
    setLine1(project.site_address?.line1 ?? '');
    setLine2(project.site_address?.line2 ?? '');
    setCity(project.site_address?.city ?? '');
    setState(project.site_address?.state ?? '');
    setPostalCode(project.site_address?.postal_code ?? '');
    setLatitude(project.site_address?.latitude ?? null);
    setLongitude(project.site_address?.longitude ?? null);
    setContactName(project.site_contact_name ?? '');
    setContactPhone(project.site_contact_phone ?? '');
    setSiteNotes(project.site_notes ?? '');
    setIntakeNotes(project.intake_notes ?? '');
    setBudgetDollars(centsToDollars(project.delivery_install_budget_cents));
    setActualDollars(centsToDollars(project.actual_cost_cents));
  }, [open, project]);

  // Build the site_address object from the line fields, preserving the existing
  // country + geocoded lat/lng (the form doesn't edit those). A site with a
  // line1 is kept; clearing line1 drops the address entirely.
  function buildSiteAddress(): ProjectUpdateInput['site_address'] {
    const l1 = line1.trim();
    if (!l1) return null;
    return {
      line1: l1,
      line2: line2.trim() || null,
      city: city.trim(),
      state: state.trim().toUpperCase(),
      postal_code: postalCode.trim(),
      country: project.site_address?.country ?? 'US',
      latitude,
      longitude,
    };
  }

  function save() {
    // requested_start_date + deadline_date are required-at-create, so the schema
    // types them as non-nullable — omit them when blank rather than sending null
    // (requested_end_date is genuinely nullable and can clear). Everything else
    // is in the partial update schema.
    const payload: ProjectUpdateInput = {
      title: title.trim(),
      client_name: clientName.trim(),
      priority,
      requested_end_date: dateInputToIso(endDate),
      site_address: buildSiteAddress(),
      site_contact_name: contactName.trim() || null,
      site_contact_phone: contactPhone.trim() || null,
      site_notes: siteNotes.trim() || null,
      intake_notes: intakeNotes.trim() || null,
      delivery_install_budget_cents: dollarsToCents(budgetDollars),
      actual_cost_cents: dollarsToCents(actualDollars),
    };
    const startIso = dateInputToIso(startDate);
    if (startIso) payload.requested_start_date = startIso;
    const deadlineIso = dateInputToIso(deadline);
    if (deadlineIso) payload.deadline_date = deadlineIso;

    update.mutate(payload, {
      onSuccess: () => {
        toast.success('Project updated');
        onClose();
      },
      onError: (e) => toast.error(e.message || 'Could not save changes'),
    });
  }

  const canSave = title.trim().length > 0 && !update.isPending;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Edit project"
      busy={update.isPending}
      className="max-w-2xl"
    >
      <div className="p-4 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="edit-project-title">Title</Label>
            <Input
              id="edit-project-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Project title"
              aria-invalid={title.trim().length === 0}
            />
          </div>
          <div>
            <Label htmlFor="edit-project-client">Client name</Label>
            <Input
              id="edit-project-client"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Client / company"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="edit-project-priority">Priority</Label>
          <select
            id="edit-project-priority"
            value={priority}
            onChange={(e) => setPriority(e.target.value as Priority)}
            className="flex h-9 w-full rounded-md border border-line bg-bg px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
          >
            {PRIORITY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label htmlFor="edit-project-start">Requested start</Label>
            <Input
              id="edit-project-start"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="edit-project-end">Requested end</Label>
            <Input
              id="edit-project-end"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="edit-project-deadline">Hard deadline</Label>
            <Input
              id="edit-project-deadline"
              type="date"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
            />
          </div>
        </div>

        <div className="border-t border-line pt-4 space-y-3">
          <Label className="mb-0">Site address</Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label htmlFor="edit-project-line1">Address line 1</Label>
              <AddressAutocomplete
                id="edit-project-line1"
                value={line1}
                onChange={setLine1}
                onSelect={(s: AddressSuggestion) => {
                  setLine1(s.line1);
                  if (s.city) setCity(s.city);
                  if (s.state) setState(s.state);
                  if (s.postal_code) setPostalCode(s.postal_code);
                  setLatitude(s.latitude ?? null);
                  setLongitude(s.longitude ?? null);
                }}
                placeholder="Start typing a street address…"
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="edit-project-line2">Address line 2</Label>
              <Input
                id="edit-project-line2"
                value={line2}
                onChange={(e) => setLine2(e.target.value)}
                placeholder="Suite, floor (optional)"
              />
            </div>
            <div>
              <Label htmlFor="edit-project-city">City</Label>
              <Input
                id="edit-project-city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="edit-project-state">State</Label>
                <Input
                  id="edit-project-state"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  maxLength={2}
                  placeholder="TX"
                />
              </div>
              <div>
                <Label htmlFor="edit-project-postal">Postal code</Label>
                <Input
                  id="edit-project-postal"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  placeholder="75001"
                />
              </div>
            </div>
          </div>
          <p className="text-[11px] text-ink-faint">
            Start typing line 1 to search addresses; picking a suggestion fills city/state/ZIP and
            refreshes the map coordinates. Clearing line 1 removes the site address.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="edit-project-contact-name">Site contact name</Label>
            <Input
              id="edit-project-contact-name"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              placeholder="On-site contact"
            />
          </div>
          <div>
            <Label htmlFor="edit-project-contact-phone">Site contact phone</Label>
            <Input
              id="edit-project-contact-phone"
              type="tel"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              placeholder="(555) 123-4567 ext. 12"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="edit-project-site-notes">Site notes</Label>
          <Textarea
            id="edit-project-site-notes"
            rows={3}
            value={siteNotes}
            onChange={(e) => setSiteNotes(e.target.value)}
            placeholder="Gate codes, parking, dock access…"
          />
        </div>

        <div>
          <Label htmlFor="edit-project-intake-notes">Intake notes</Label>
          <Textarea
            id="edit-project-intake-notes"
            rows={3}
            value={intakeNotes}
            onChange={(e) => setIntakeNotes(e.target.value)}
            placeholder="Scope, special instructions…"
          />
        </div>

        <div className="border-t border-line pt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="edit-project-budget">Proposed budget ($)</Label>
            <Input
              id="edit-project-budget"
              type="number"
              min={0}
              step="0.01"
              value={budgetDollars}
              onChange={(e) => setBudgetDollars(e.target.value)}
              placeholder="0.00"
            />
          </div>
          <div>
            <Label htmlFor="edit-project-actual">Actual cost ($)</Label>
            <Input
              id="edit-project-actual"
              type="number"
              min={0}
              step="0.01"
              value={actualDollars}
              onChange={(e) => setActualDollars(e.target.value)}
              placeholder="0.00"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={onClose} disabled={update.isPending}>
            Cancel
          </Button>
          <Button size="sm" onClick={save} disabled={!canSave}>
            {update.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
